/**
 * @fileoverview Header component for the Storyteller web application.
 * Displays the application logo, stepper, and share button.
 *
 * @example
 * // Basic usage
 * <Header assessments={assessments} batteryId="123" />
 */

import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import Logo from './assets/Logo.svg'
import { useFeatureFlag } from './hooks/useFeatureFlag'
import { ModalContext } from './ModalContext'
import { ShareModal } from './ShareModal'
import { Stepper } from './Stepper'

/**
 * @interface HeaderProps
 * @description Props for the Header component.
 * @property {Novoic.Assessment[]} assessments - The assessments to display in the stepper.
 * @property {string} batteryId - The ID of the battery to share.
 */
interface HeaderProps {
    assessments: Novoic.Assessment[]
    batteryId: string
}

/**
 * @function Header
 * @description Header component for the Storyteller web application.
 * Displays the application logo, stepper, and share button.
 *
 * @example
 * // Basic usage
 * <Header assessments={assessments} batteryId="123" />
 */
export function Header(props: HeaderProps) {
    const assessments = props.assessments.filter((assessment) => !assessment.annotation?.hidden)
    const modal = useContext(ModalContext)
    const { t } = useTranslation()

    const shareConfig = useFeatureFlag<{ invitation_url: string }>(props.batteryId)

    function handleShareOpen() {
        modal.open({
            render: () => <ShareModal shareConfig={shareConfig} />,
        })
    }

    return (
        <div className="header">
            <Logo className="header-logo" />
            <Stepper
                assessments={assessments}
                shareConfig={shareConfig}
                onShare={handleShareOpen}
            />
            <div className="header-logo">
                {shareConfig && (
                    <button className="button confirm" onClick={handleShareOpen}>
                        {t('share.cta')}
                    </button>
                )}
            </div>
        </div>
    )
}
